/**
 * Saved Campaign Service
 * 
 * This service is now a thin wrapper around the unified save service.
 * All campaign operations go through the unified service for consistency.
 */

import { supabase } from '@/integrations/supabase/client';
import { saveCampaignUnified, type CampaignSaveData } from '../unifiedCampaignSaveService';

export const saveCampaignToDatabase = async (campaignData: any) => {
  console.log('saveCampaignToDatabase called - using unified service');
  
  // Map the data to the unified format
  const unifiedData: CampaignSaveData = {
    campaignId: campaignData.campaign_id,
    campaignName: campaignData.campaign_name,
    companyName: campaignData.company_name || '',
    brandName: campaignData.brand_name || '',
    personInCharge: campaignData.person_in_charge || '',
    jobTitle: campaignData.job_title || '',
    email: campaignData.email || '',
    mobile: campaignData.mobile || '',
    selectedLocations: campaignData.selected_locations || [],
    creativeUrls: campaignData.creative_urls || [],
    creativeMetadata: Array.isArray(campaignData.creative_metadata)
      ? campaignData.creative_metadata
      : Array.isArray(campaignData.creativeMetadata)
        ? campaignData.creativeMetadata
        : (campaignData.creative_urls || []).map((url: string, i: number) => ({
            url,
            creativeId: `creative_${i}`,
            locationName: '',
            orientation: 'landscape' as const,
            scheduledDays: [],
          })),
    totalDevices: campaignData.devices_count || 0,
    durationDays: campaignData.campaign_parameters?.duration || 7,
    startDate: campaignData.start_date,
    endDate: campaignData.end_date,
    totalBudget: campaignData.total_budget || 0,
    budgetCalculations: campaignData.budget_info,
    termsAccepted: campaignData.terms_accepted ?? true,
    paymentType: campaignData.payment_type,
    status: campaignData.status,
    userId: campaignData.user_id,
    creatorEmail: campaignData.creator_email,
  };
  
  const result = await saveCampaignUnified(unifiedData);
  
  return {
    success: result.success,
    id: result.campaignId,
    data: result.success ? { id: result.campaignId } : null,
    error: result.error,
  };
};

export const getCampaignFromDatabase = async (id: string) => {
  console.log('getCampaignFromDatabase called with id:', id);
  try {
    const { data, error } = await supabase
      .from('saved_campaigns')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      console.error('Error getting campaign:', error);
      return null;
    }
    
    if (!data) {
      return null;
    }

    return data;
  } catch (err) {
    console.error('Exception in getCampaignFromDatabase:', err);
    return null;
  }
};

export const updateCampaignInDatabase = async (id: string, updateData: any) => {
  console.log('updateCampaignInDatabase called with:', id, updateData);
  try {
    const { data, error } = await supabase
      .from('saved_campaigns')
      .update(updateData)
      .eq('id', id)
      .select()
      .maybeSingle();

    if (error) {
      console.error('Error updating campaign:', error);
      return { success: false, data: null, error };
    }
    
    if (!data) {
      return { success: false, data: null, error: new Error('Campaign not found') };
    }

    return { success: true, data, error: null };
  } catch (err) {
    console.error('Exception in updateCampaignInDatabase:', err);
    return { success: false, data: null, error: err };
  }
};

export const createSavedCampaignInDb = async (data: any) => {
  console.log('createSavedCampaignInDb called - using unified service');
  
  // Map the data to the unified format
  const unifiedData: CampaignSaveData = {
    campaignId: data.campaign_id,
    campaignName: data.campaign_name,
    companyName: data.company_name || '',
    brandName: data.brand_name || '',
    personInCharge: data.person_in_charge || '',
    jobTitle: data.job_title || '',
    email: data.email || '',
    mobile: data.mobile || '',
    selectedLocations: data.selected_locations || [],
    creativeUrls: data.creative_urls || [],
    // pass through metadata when available (saveCampaignUnified also has a safe fallback)
    creativeMetadata: Array.isArray(data.creative_metadata)
      ? data.creative_metadata
      : Array.isArray(data.creativeMetadata)
        ? data.creativeMetadata
        : undefined,
    totalDevices: data.devices_count || 0,
    durationDays: data.campaign_parameters?.duration || 7,
    startDate: data.start_date,
    endDate: data.end_date,
    totalBudget: data.total_budget || 0,
    budgetCalculations: data.budget_info,
    termsAccepted: data.terms_accepted ?? true,
    paymentType: data.payment_type,
    status: data.status,
    userId: data.user_id,
    creatorEmail: data.creator_email,
  };
  
  const result = await saveCampaignUnified(unifiedData);
  
  return {
    success: result.success,
    id: result.campaignId,
    data: result.success ? { id: result.campaignId, campaign_id: result.campaignId } : null,
    error: result.error,
  };
};
